import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { PRIOS } from "../constants";
import PrioIcon from "./PrioIcon";
import "./TaskQuickMenu.css";

export default function TaskQuickMenu({ task, employees, onUpdate, onDuplicate, onDelete, onClose, pos }) {
  const ref = useRef(null);
  const [subMenu, setSubMenu] = useState(null);
  const hideTimeout = useRef(null);

  // Only active staff is assignable. Archived people already on this task stay
  // listed (captured when the menu opens, so removing one is still undoable)
  // instead of dropping out of sight together with their assignment.
  const initialPersonIds = useRef(task.person_ids || []);
  const personChoices = useMemo(() => {
    const list = employees || [];
    return [
      ...list.filter((m) => !m.archived),
      ...list.filter((m) => m.archived && initialPersonIds.current.includes(m.id)),
    ];
  }, [employees]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  const handleSubMenuEnter = (menu) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setSubMenu(menu);
  };

  const handleSubMenuLeave = () => {
    hideTimeout.current = setTimeout(() => setSubMenu(null), 150);
  };

  // submenu opens to the right by default — flip it left if it would run off-screen
  // (only on desktop; on mobile the submenu is repositioned to the bottom via CSS)
  const flipSub = window.innerWidth > 768 && pos.left + 200 > window.innerWidth;
  const subMenuClass = `quick-sub-menu ${flipSub ? "quick-sub-menu-flip" : ""}`;

  const handlePrioChange = (prio) => {
    onUpdate(task.id, { prio });
    onClose();
  };

  // The menu stays open between clicks, but the task prop only refreshes after a
  // server round-trip — computing "next" from the prop made the second of two
  // quick clicks overwrite the first assignment. Track the selection locally
  // (optimistically) and re-sync whenever the prop actually updates.
  const [personIds, setPersonIds] = useState(task.person_ids || []);
  useEffect(() => { setPersonIds(task.person_ids || []); }, [task.person_ids]);

  const togglePerson = (personId) => {
    const next = personIds.includes(personId)
      ? personIds.filter((id) => id !== personId)
      : [...personIds, personId];
    setPersonIds(next);
    onUpdate(task.id, { person_ids: next });
  };

  const clearPersons = () => {
    setPersonIds([]);
    onUpdate(task.id, { person_ids: [] });
    onClose();
  };

  return ReactDOM.createPortal(
    <>
      <div className="quick-menu" ref={ref} style={{ top: pos.top, left: pos.left, transform: 'translateX(-100%)' }}>
        <div className="quick-menu-item-wrap"
          onMouseEnter={() => handleSubMenuEnter("prio")}
          onMouseLeave={handleSubMenuLeave}>
          <button className="quick-menu-item">
            <span className="quick-menu-icon"><PrioIcon prio={task.prio} size={14} /></span>
            Priorität ändern
            <span className="quick-menu-arrow">›</span>
          </button>
          {subMenu === "prio" && (
            <div className={subMenuClass}>
              <div className="quick-sub-menu-inner">
                {PRIOS.map((p) => (
                  <button
                    key={p.value}
                    className={`quick-menu-item ${task.prio === p.value ? "quick-menu-active" : ""}`}
                    onClick={() => handlePrioChange(p.value)}
                  >
                    <span className="quick-menu-icon"><PrioIcon prio={p.value} size={14} /></span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="quick-menu-item-wrap"
          onMouseEnter={() => handleSubMenuEnter("person")}
          onMouseLeave={handleSubMenuLeave}>
          <button className="quick-menu-item">
            <span className="quick-menu-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
            Person ändern
            <span className="quick-menu-arrow">›</span>
          </button>
          {subMenu === "person" && (
            <div className={subMenuClass}>
              <div className="quick-sub-menu-inner">
                <button
                  className={`quick-menu-item ${!personIds.length ? "quick-menu-active" : ""}`}
                  onClick={clearPersons}
                >
                  — Keine —
                </button>
                {personChoices.map((m) => (
                  <button
                    key={m.id}
                    className={`quick-menu-item ${personIds.includes(m.id) ? "quick-menu-active" : ""}`}
                    onClick={() => togglePerson(m.id)}
                  >
                    {m.archived ? `${m.name} (archiviert)` : m.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="quick-menu-divider" />
        <button className="quick-menu-item" onClick={() => { onDuplicate(task); onClose(); }}>
          <span className="quick-menu-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </span>
          Duplizieren
        </button>
        {task.calendar_date && (
          <button className="quick-menu-item quick-menu-warning" onClick={() => { onUpdate(task.id, { calendar_date: null }); onClose(); }}>
            <span className="quick-menu-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="9" y1="14" x2="15" y2="20" />
                <line x1="15" y1="14" x2="9" y2="20" />
              </svg>
            </span>
            Aus Kalender entfernen
          </button>
        )}
        <button className="quick-menu-item quick-menu-danger" onClick={() => { onDelete(task); }}>
          <span className="quick-menu-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </span>
          Löschen
        </button>
      </div>
    </>,
    document.body
  );
}
